import { useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ModalContext } from './Modal'
import { registerMenuEscape } from './overlayStack'
import { alleAuswaehlen, alleGewaehlt, gefiltert, keineAuswaehlen, zeigeSammelchip } from './multiSelectSuche'

export interface MultiSelectOption {
  value: string
  label: string
  /** Second muted line under the label — menu only, never on chips/trigger. */
  note?: string
  /** Renders like the at-max `blocked` style — dimmed, non-toggleable. */
  disabled?: boolean
  /** Optional element before the label — menu rows AND chips (e.g.
   *  <CountryFlags> next to a language). Not included in search matching. */
  leading?: ReactNode
  /** Large variant only: short muted text at the right end of the row (e.g. the school type). */
  trailing?: string
  /** Quick-filter tags (e.g. 'GS', 'WFS'): a pressed quick filter keeps the rows that carry one of the pressed tags. */
  tags?: readonly string[]
}

interface MultiSelectProps {
  label?: string
  /** Accessible name when no visible label is rendered (inline filter rows). */
  ariaLabel?: string
  options: readonly MultiSelectOption[]
  /** Selected values. */
  selected: readonly string[]
  onChange: (values: string[]) => void
  placeholder?: string
  searchable?: boolean
  disabled?: boolean
  /** Compact size — for inline filters next to text. Default = full size for forms. */
  size?: 'default' | 'compact'
  /** `surface` (default) on flat pages; `glass` for Liquid Glass surfaces. Mirrors Dropdown. */
  variant?: 'surface' | 'glass'
  /** `inline` (default) expands the panel in flow so overflow-hidden parents never clip it.
   *  `float` overlays the panel (absolute) — use on parents that don't clip (GlassCard).
   *  Pairs with `placement` to pick which side the floated panel opens toward. */
  menu?: 'inline' | 'float'
  /** Only meaningful with `menu="float"`. `down` (default) opens below the trigger
   *  (`mt-1`); `up` opens above it (`bottom-full mb-1`) — use when the trigger sits
   *  near the bottom of a clipping container. */
  placement?: 'down' | 'up'
  /** Only meaningful with `menu="float"`. Render the floating panel via React
   *  Portal to document.body, fixed-positioned off the trigger's own rect,
   *  instead of `absolute` inside this component's own relative wrapper.
   *  Defaults to `true` automatically inside a <Modal> when `menu="float"`
   *  (its scroll container clips an in-flow absolute panel) — pass this
   *  explicitly to override that default. `menu="inline"` is unaffected: it
   *  expands in normal flow and the Modal body simply scrolls to it. */
  usePortal?: boolean
  /** Only meaningful with `menu="float"` + portal rendering. Default 1000, or
   *  90 when the panel defaults to portal mode inside a <Modal>. */
  menuZIndex?: number
  /** Cap on the number of selectable values; remaining options disable at the cap. */
  max?: number
  /** Search input placeholder — also doubles as its accessible name (no separate
   *  visible label). Default = German for callers that haven't localized yet. */
  searchPlaceholder?: string
  /** Empty-state copy shown when the search query matches nothing. */
  noResultsLabel?: string
  /** Accessible label for a selected chip's remove button, given the option's label. */
  removeLabel?: (label: string) => string
  /** From `sammelAb` selections (default 4) the trigger shows ONE summary chip (`sammelText(n)`, e.g. "12 Schulen gewählt")
   *  instead of one chip per selection. Without `sammelText` every selection stays its own chip (old behaviour). */
  sammelText?: (n: number) => string
  sammelAb?: number
  /** Accessible name of the summary chip's clear action (clears the whole selection). */
  clearLabel?: string
  /** "Alle" / "Keine" over the FILTERED rows (search + quick filter), for lists of 100+ rows. */
  bulk?: boolean
  alleLabel?: string
  keineLabel?: string
  /** Quick-filter toggles above the list (needs `tags` on the options), e.g. GS / WFS. */
  schnellFilter?: ReadonlyArray<{ tag: string; label: string }>
  /**
   * Large variant for 100+ rows on touch screens (Schulportal / office school filter, prototype .msp): rows 48 px with `trailing` text,
   * 44 px search and quick-filter targets, a count line under the list (`zaehlText`) and, at 520 px or less, a bottom sheet with a "Fertig" button
   * (`fertigLabel`) instead of the small float menu.
   */
  large?: boolean
  /** Large variant: the count line under the list, e.g. "112 von 112 Schulen · 3 gewählt". Not shown without it. */
  zaehlText?: (sichtbar: number, gesamt: number, gewaehlt: number) => string
  fertigLabel?: string
  /** Large variant: muted second part of the label, e.g. "· 112 Standorte" (prototype .lbl-f .sub). */
  labelSub?: string
}

/**
 * Searchable multi-select. Selected values render as removable chips on the
 * trigger. Trigger styling mirrors Dropdown (surface/glass × default/compact) so
 * both controls read as one vocabulary when they share a filter row.
 */
export function MultiSelect({
  label, ariaLabel, options, selected, onChange,
  placeholder = 'Auswählen…', searchable = true, disabled = false,
  size = 'default', variant = 'surface', menu = 'inline', placement = 'down', max,
  searchPlaceholder = 'Suchen…', noResultsLabel = 'Keine Treffer',
  removeLabel = (optLabel: string) => `${optLabel} entfernen`,
  usePortal, menuZIndex,
  sammelText, sammelAb = 4, clearLabel = 'Auswahl leeren',
  bulk = false, alleLabel = 'Alle', keineLabel = 'Keine', schnellFilter,
  large = false, zaehlText, fertigLabel = 'Fertig', labelSub,
}: MultiSelectProps) {
  const insideModal = useContext(ModalContext)
  // Large variant on a phone (<= 520 px): bottom sheet, always portalled.
  const [schmal, setSchmal] = useState(() => large && typeof window !== 'undefined' && window.matchMedia?.('(max-width: 520px)').matches === true)
  useEffect(() => {
    if (!large || typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(max-width: 520px)')
    const ab = () => setSchmal(mq.matches)
    ab()
    mq.addEventListener('change', ab)
    return () => mq.removeEventListener('change', ab)
  }, [large])
  const blatt = large && schmal
  const effectiveUsePortal = blatt || (menu === 'float' && (usePortal ?? insideModal))
  const effectiveMenuZIndex = menuZIndex ?? (insideModal ? 90 : 1000)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [schnell, setSchnell] = useState<ReadonlySet<string>>(() => new Set())
  const rootRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const labelId = useId()
  const selectedSet = new Set(selected)
  const compact = size === 'compact'
  const atMax = max != null && selected.length >= max

  // Search is umlaut-tolerant (Mühle = Muhle = Muehle, ß = ss); quick filters narrow by tag first.
  const filtered = useMemo(() => gefiltert(options, searchable ? query : '', schnell), [options, searchable, query, schnell])

  // Floating panel: close on outside click (inline panels stay as they are).
  useEffect(() => {
    if (!open || (menu !== 'float' && !blatt)) return
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (rootRef.current?.contains(target)) return
      if (effectiveUsePortal && menuRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, menu, blatt, effectiveUsePortal])

  // Large variant (spec C): on a phone the sheet opens with the first row focused (the keyboard stays closed); closing returns the focus to the trigger.
  const warOffen = useRef(false)
  useEffect(() => {
    if (!large) return
    if (open && blatt) menuRef.current?.querySelector<HTMLElement>('[role="option"]')?.focus({ preventScroll: true })
    if (!open && warOffen.current) openerRef.current?.focus({ preventScroll: true })
    warOffen.current = open
  }, [open, blatt, large])

  // Escape schließt NUR das Menü, nie den Drawer/das Modal darum (overlayStack: das offene Menü steht obenauf) — auch das
  // eingeklappte Feld im Fluss (menu="inline"): vorher nahm es Escape gar nicht an, und der Editor schloss samt Auswahl.
  useEffect(() => (open ? registerMenuEscape(() => setOpen(false)) : undefined), [open])

  // Portal path only: position the panel off the trigger's own rect with
  // fixed coordinates, same recipe as Dropdown's usePortal — re-measured on
  // open + resize/scroll so it stays anchored while the Modal body scrolls.
  const [floatRect, setFloatRect] = useState<{ top?: number; bottom?: number; left: number; width: number; maxHeight: number } | null>(null)
  useLayoutEffect(() => {
    if (!open || !effectiveUsePortal || blatt) return
    const measure = () => {
      const trigger = rootRef.current
      if (!trigger) return
      const r = trigger.getBoundingClientRect()
      const GAP = 4
      const EDGE = 8
      const spaceBelow = window.innerHeight - r.bottom - GAP - EDGE
      const spaceAbove = r.top - GAP - EDGE
      const openUp = placement === 'up' || (spaceBelow < 180 && spaceAbove > spaceBelow)
      const maxHeight = Math.max(160, Math.floor(openUp ? spaceAbove : spaceBelow))
      setFloatRect({
        left: r.left,
        width: r.width,
        ...(openUp ? { bottom: window.innerHeight - r.top + GAP } : { top: r.bottom + GAP }),
        maxHeight,
      })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, effectiveUsePortal, blatt, placement])

  const toggle = (value: string) => {
    // A selected value must always stay removable, even if it's since become
    // `disabled` (e.g. no longer configured) — the disabled gate below only
    // blocks *adding* a new disabled option, never removing an existing one.
    if (selectedSet.has(value)) { onChange(selected.filter((v) => v !== value)); return }
    if (options.find((o) => o.value === value)?.disabled) return
    if (!atMax) onChange([...selected, value])
  }

  const selectedOpts = options.filter((o) => selectedSet.has(o.value))
  const sammel = !!sammelText && zeigeSammelchip(selected.length, sammelAb)

  // Trigger — same recipe as Dropdown's surface/glass × default/compact triggers.
  const shape = compact ? 'min-h-[30px] px-2.5 py-1 rounded-lg text-xs' : 'min-h-[2.75rem] px-3 py-2 rounded-xl text-sm'
  const skin = variant === 'glass'
    ? `bg-white/50 dark:bg-slate-900/45 backdrop-blur ${open ? 'border-primary/60 bg-white/85 dark:bg-slate-900/75' : 'border-white/50 dark:border-white/15 bg-white/60 dark:bg-slate-900/55 hover:border-primary/40 hover:bg-white/65 dark:hover:bg-slate-900/60'}`
    : `bg-surface-container-low ${open ? 'border-primary ring-4 ring-primary/5' : 'border-outline-variant hover:border-primary/30'}`
  // Large variant (prototype .msel-box, spec C1): 52 px box, 14 px radius, card fill, 1 px outline (2 px primary when open), gap 6.
  const triggerCls = large
    ? `w-full text-on-surface border bg-card min-h-[52px] rounded-[14px] py-1 pl-2.5 pr-1 flex flex-wrap items-center gap-1.5 ${open ? 'border-primary ring-1 ring-primary' : 'border-outline-variant'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
    : `w-full text-on-surface border flex flex-wrap items-center gap-1 transition-all ${shape} ${skin} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
  const chipCls = compact
    ? 'inline-flex items-center gap-0.5 rounded bg-primary/10 pl-1.5 pr-1 py-px text-[11px] font-medium text-on-surface max-w-[150px]'
    : 'inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-on-surface max-w-[220px]'
  const panelCls = `rounded-xl border border-outline-variant bg-surface-container-lowest ${large ? 'p-0 flex flex-col overflow-hidden sp-flfade !rounded-[20px] !bg-card/95 backdrop-blur-[24px] min-w-[360px]' : 'p-1.5'} ${
    blatt
      ? 'fixed inset-x-2 bottom-[calc(8px+env(safe-area-inset-bottom,0px))] h-[min(600px,calc(100dvh-88px))] !rounded-[22px] !min-w-0 shadow-[0_-12px_40px_-8px_rgba(15,23,42,0.3)]'
      : menu === 'float'
      ? effectiveUsePortal
        ? `min-w-[280px] ${large ? 'shadow-[0_24px_50px_-12px_rgba(15,23,42,0.3)]' : 'shadow-lg'}`
        : `absolute left-0 right-0 z-30 min-w-[280px] ${large ? 'shadow-[0_24px_50px_-12px_rgba(15,23,42,0.3)]' : 'shadow-lg'} ${placement === 'up' ? 'bottom-full mb-1' : 'mt-1'}`
      : ''
  }`

  const sammelGross = large && !!sammelText && zeigeSammelchip(selected.length, sammelAb)
  return (
    <div ref={rootRef} className={`${compact ? '' : 'space-y-1.5'} ${menu === 'float' ? 'relative' : ''} ${large ? 'max-w-[560px]' : ''}`}>
      {label && (large
        ? <label id={labelId} className="block text-[17px] text-on-surface">{label}{labelSub && <span className="text-on-surface-variant"> {labelSub}</span>}</label>
        : <label id={labelId} className="block text-[10px] font-medium uppercase tracking-wider text-on-surface-variant">{label}</label>)}
      {/* The styled box is a div so chips can be real buttons (no interactive nesting);
          the opener fills the remaining space and carries the accessible name. */}
      <div className={triggerCls}>
        {sammel && sammelText && !large && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange([])}
            className={`${chipCls} outline-none focus-visible:ring-2 focus-visible:ring-primary/30`}
            aria-label={`${sammelText(selected.length)}. ${clearLabel}`}
          >
            <span className="truncate">{sammelText(selected.length)}</span>
            <span className="material-symbols-outlined shrink-0" style={{ fontSize: compact ? 12 : 13 }} aria-hidden="true">close</span>
          </button>
        )}
        {!sammel && !large && selectedOpts.map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            onClick={() => toggle(o.value)}
            className={`${chipCls} outline-none focus-visible:ring-2 focus-visible:ring-primary/30`}
            title={o.label}
            aria-label={removeLabel(o.label)}
          >
            {o.leading ? <span className="shrink-0 inline-flex items-center">{o.leading}</span> : null}
            <span className="truncate">{o.label}</span>
            <span className="material-symbols-outlined shrink-0" style={{ fontSize: compact ? 12 : 13 }} aria-hidden="true">close</span>
          </button>
        ))}
        {/* Large: 1 to 3 selections are chips (36 px, remove button 44 px); from 4 the trigger text itself says "N Schulen gewählt". */}
        {large && !sammelGross && selectedOpts.map((o) => (
          <span key={o.value} title={o.label}
            className="inline-flex h-9 max-w-[200px] items-center whitespace-nowrap rounded-full bg-[color-mix(in_srgb,var(--color-primary)_16%,var(--color-card))] pl-3 text-[14px] font-semibold text-on-surface">
            {o.leading ? <span className="mr-1 inline-flex shrink-0 items-center">{o.leading}</span> : null}
            <span className="truncate">{o.label}</span>
            <button type="button" disabled={disabled} onClick={() => toggle(o.value)} aria-label={removeLabel(o.label)}
              className="-my-1 -mr-1 -ml-1.5 grid size-11 shrink-0 place-items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden="true">close</span>
            </button>
          </span>
        ))}
        <button
          ref={openerRef}
          type="button"
          id={large ? 'msel-t' : undefined}
          disabled={disabled}
          aria-labelledby={label ? labelId : undefined}
          aria-label={label ? undefined : ariaLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((v) => !v)}
          className={large
            ? 'flex min-h-11 min-w-0 flex-[1_1_120px] items-center justify-end gap-2 self-stretch rounded-xl pl-1 pr-1.5 text-left text-[16px] font-semibold text-on-surface outline-hidden focus-visible:outline-solid focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-primary'
            : 'flex min-w-[1.5rem] flex-1 items-center justify-end gap-2 self-stretch text-left outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded'}
        >
          {large ? (
            sammelGross && sammelText
              ? <span className="mr-auto truncate">{sammelText(selected.length)}</span>
              : <span className={`truncate ${selectedOpts.length ? 'sr-only' : 'mr-auto font-medium text-on-surface-variant'}`}>{placeholder}</span>
          ) : (
            <span className={`truncate ${selectedOpts.length ? 'sr-only' : 'mr-auto text-on-surface-variant'}`}>{placeholder}</span>
          )}
          <span className={`material-symbols-outlined shrink-0 ${large ? '!text-[24px] text-on-surface-variant transition-transform duration-200 ease-[cubic-bezier(0.2,0.8,0.2,1)]' : '!text-[16px] text-on-surface transition-transform'} ${open ? 'rotate-180' : ''}`}>expand_more</span>
        </button>
      </div>

      {open && renderPanel()}
    </div>
  )

  /** Large variant (prototype .msp): search 48 px, quick filters + Alle/Keine 44 px, rows 48 px with the type at the right, count line, "Fertig" on the phone sheet. */
  function renderGross() {
    const touch = 'h-11 outline-hidden focus-visible:outline-solid focus-visible:outline-[3px] focus-visible:outline-primary focus-visible:outline-offset-2'
    return (
      <>
        <div className="flex flex-col gap-2 border-b border-outline-variant/60 p-2.5">
          {searchable && (
            <label className="flex h-12 items-center gap-2 rounded-xl bg-glass-a px-3 ring-1 ring-inset ring-outline-variant focus-within:ring-2 focus-within:ring-primary">
              <span className="material-symbols-outlined !text-[20px] text-on-surface-variant" aria-hidden="true">search</span>
              <input
                type="search"
                value={query}
                autoFocus={menu === 'float' && !blatt}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                autoComplete="off"
                name="ui-search"
                data-lpignore="true"
                data-1p-ignore="true"
                className="min-h-11 min-w-0 flex-1 bg-transparent text-base text-on-surface outline-none placeholder:text-on-surface-variant"
              />
            </label>
          )}
          {(schnellFilter?.length || bulk) && (
            <div className="flex items-center gap-1.5">
              {schnellFilter?.map((f) => {
                const an = schnell.has(f.tag)
                return (
                  <button
                    key={f.tag}
                    type="button"
                    aria-pressed={an}
                    onClick={() => setSchnell((alt) => { const neu = new Set(alt); if (neu.has(f.tag)) neu.delete(f.tag); else neu.add(f.tag); return neu })}
                    className={`${touch} min-w-14 rounded-full px-3.5 text-[14px] font-bold ${an ? 'bg-primary text-on-primary' : 'ring-1 ring-inset ring-outline-variant text-on-surface hover:bg-surface-container-low'}`}
                  >{f.label}</button>
                )
              })}
              <span className="flex-1" />
              {bulk && (
                <>
                  <button type="button" disabled={atMax || alleGewaehlt(selected, filtered)} onClick={() => onChange(alleAuswaehlen(selected, filtered, max))}
                    className={`${touch} rounded-lg px-3 text-[15px] font-bold text-primary hover:bg-surface-container-low disabled:opacity-40`}>{alleLabel}</button>
                  <button type="button" disabled={selected.length === 0} onClick={() => onChange(keineAuswaehlen(selected, filtered))}
                    className={`${touch} rounded-lg px-3 text-[15px] font-bold text-primary hover:bg-surface-container-low disabled:opacity-40`}>{keineLabel}</button>
                </>
              )}
            </div>
          )}
        </div>
        <div
          className={`overflow-y-auto overscroll-contain p-1 ${blatt ? 'min-h-0 flex-1' : 'min-h-[272px] max-h-[max(272px,min(352px,46vh))] flex-1'}`}
          role="listbox" aria-multiselectable="true"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm italic text-on-surface-variant" role="status">{noResultsLabel}</div>
          ) : (
            filtered.map((o) => {
              const on = selectedSet.has(o.value)
              const blocked = (!on && atMax) || !!o.disabled
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={on}
                  aria-disabled={blocked ? 'true' : undefined}
                  disabled={blocked}
                  onClick={() => toggle(o.value)}
                  className={`flex h-11 w-full items-center gap-2.5 rounded-[10px] px-2.5 text-left text-[15px] text-on-surface outline-hidden focus-visible:outline-solid focus-visible:outline-[3px] focus-visible:outline-primary focus-visible:[outline-offset:-3px] ${blocked ? 'cursor-not-allowed opacity-40' : 'hover:bg-glass-a'}`}
                >
                  <span className={`grid size-[22px] shrink-0 place-items-center rounded-md ${on ? 'bg-primary text-on-primary' : 'ring-2 ring-inset ring-outline-variant'}`}>
                    {on && <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden="true">check</span>}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{o.label}</span>
                    {o.note && <span className="block truncate text-xs text-on-surface-variant">{o.note}</span>}
                  </span>
                  {o.trailing && <small className="shrink-0 text-xs font-bold text-on-surface-variant">{o.trailing}</small>}
                </button>
              )
            })
          )}
        </div>
        {(zaehlText || blatt) && (
          <div className={`flex items-center gap-2 border-t border-outline-variant/60 py-2 pl-3 ${blatt ? 'pr-2' : 'pr-3'}`}>
            <div id="msc" className="flex-1 text-[13px] text-on-surface-variant tabular-nums" aria-live="polite">{zaehlText ? zaehlText(filtered.length, options.length, selected.length) : ''}</div>
            {blatt && (
              <button type="button" onClick={() => setOpen(false)}
                className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full bg-primary px-5 text-[16px] font-semibold text-on-primary outline-hidden focus-visible:outline-solid focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-primary">
                <span className="material-symbols-outlined !text-[22px]" aria-hidden="true">check</span>{fertigLabel}
              </button>
            )}
          </div>
        )}
      </>
    )
  }

  function renderPanelBody() {
    if (large) return renderGross()
    return (
      <>
        {(searchable || max != null) && (
          <div className="relative mb-1.5 flex items-center gap-2">
            {searchable && (
              <>
                <span className="material-symbols-outlined absolute left-2 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none" style={{ fontSize: 14 }} aria-hidden="true">search</span>
                <input
                  type="text"
                  value={query}
                  autoFocus={menu === 'float'}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  // See Dropdown.tsx for why: plain autoComplete="off" is a
                  // known no-op for name/address-shaped fields in Chrome.
                  autoComplete="one-time-code"
                  name="ui-search"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  className="w-full bg-surface-container-low border border-outline-variant rounded-md pl-7 pr-2 py-1.5 text-xs text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:border-primary"
                />
              </>
            )}
            {max != null && (
              <span className={`shrink-0 tabular-nums text-[11px] ${atMax ? 'font-semibold text-on-surface' : 'text-on-surface-variant'}`}>{selected.length}/{max}</span>
            )}
          </div>
        )}
        {(schnellFilter?.length || bulk) && (
          <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
            {schnellFilter?.map((f) => {
              const an = schnell.has(f.tag)
              return (
                <button
                  key={f.tag}
                  type="button"
                  aria-pressed={an}
                  onClick={() => setSchnell((alt) => { const neu = new Set(alt); if (neu.has(f.tag)) neu.delete(f.tag); else neu.add(f.tag); return neu })}
                  className={`min-h-8 rounded-full px-3 text-xs font-semibold outline-none focus-visible:ring-2 focus-visible:ring-primary/30 ${an ? 'bg-primary text-on-primary' : 'border border-outline-variant text-on-surface hover:bg-surface-container-low'}`}
                >{f.label}</button>
              )
            })}
            {bulk && (
              <span className="ml-auto flex items-center gap-1">
                <button
                  type="button"
                  disabled={atMax || alleGewaehlt(selected, filtered)}
                  onClick={() => onChange(alleAuswaehlen(selected, filtered, max))}
                  className="min-h-8 rounded-lg px-2.5 text-xs font-semibold text-primary outline-none hover:bg-surface-container-low focus-visible:ring-2 focus-visible:ring-primary/30 disabled:opacity-40"
                >{alleLabel}</button>
                <button
                  type="button"
                  disabled={selected.length === 0}
                  onClick={() => onChange(keineAuswaehlen(selected, filtered))}
                  className="min-h-8 rounded-lg px-2.5 text-xs font-semibold text-primary outline-none hover:bg-surface-container-low focus-visible:ring-2 focus-visible:ring-primary/30 disabled:opacity-40"
                >{keineLabel}</button>
              </span>
            )}
          </div>
        )}
        <div className="max-h-64 overflow-y-auto" role="listbox" aria-multiselectable="true">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs italic text-on-surface-variant text-center">{noResultsLabel}</div>
          ) : (
            filtered.map((o) => {
              const on = selectedSet.has(o.value)
              const blocked = (!on && atMax) || !!o.disabled
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={on}
                  aria-disabled={blocked ? 'true' : undefined}
                  disabled={blocked}
                  onClick={() => toggle(o.value)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-on-surface ${blocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-surface-container-low'}`}
                >
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${on ? 'border-primary bg-primary text-on-primary' : 'border-outline-variant'}`}>
                    {on && <span className="material-symbols-outlined" style={{ fontSize: 12 }} aria-hidden="true">check</span>}
                  </span>
                  <span className="flex-1 min-w-0">
                    {o.leading ? (
                      <span className="flex min-w-0 items-center gap-1.5">
                        <span className="shrink-0 inline-flex items-center">{o.leading}</span>
                        <span className="truncate">{o.label}</span>
                      </span>
                    ) : (
                      <span className="block truncate">{o.label}</span>
                    )}
                    {o.note && <span className="block text-[10px] text-on-surface-variant truncate">{o.note}</span>}
                  </span>
                </button>
              )
            })
          )}
        </div>
      </>
    )
  }

  function renderPanel() {
    if (blatt) {
      return createPortal(<div ref={menuRef} className={panelCls} role="dialog" aria-label={label ?? ariaLabel} style={{ zIndex: effectiveMenuZIndex }}>{renderPanelBody()}</div>, document.body)
    }
    if (menu === 'float' && effectiveUsePortal) {
      return createPortal(
        <div
          ref={menuRef}
          className={panelCls}
          style={
            floatRect
              ? {
                  position: 'fixed',
                  left: floatRect.left,
                  width: floatRect.width,
                  ...(floatRect.top !== undefined ? { top: floatRect.top } : {}),
                  ...(floatRect.bottom !== undefined ? { bottom: floatRect.bottom } : {}),
                  maxHeight: floatRect.maxHeight,
                  zIndex: effectiveMenuZIndex,
                }
              : { visibility: 'hidden' as const }
          }
        >
          {renderPanelBody()}
        </div>,
        document.body,
      )
    }
    return <div className={panelCls}>{renderPanelBody()}</div>
  }
}
